import { ResolutionReason } from "@sitescene/types";

import { EditWatchType } from "@/features/watchlist/types";
import posthog from "@/lib/posthog";

export type ActiveWatchLocationConflictSource = "create" | "reactivate";

export type WatchCreateSource = "edit_description" | "watchlist";

/** Capture when the one-active-watch-per-location conflict UI is shown. */
export function captureActiveWatchLocationModalOpened({
	conflictingWatchId,
	locationId,
	source,
}: {
	conflictingWatchId: string;
	locationId: string;
	source: ActiveWatchLocationConflictSource;
}) {
	posthog.capture("active_watch_location_modal_opened", {
		conflicting_watch_id: conflictingWatchId,
		location_id: locationId,
		source,
	});
}

/** Capture when resolve succeeds and the operator deactivates the linked active watch. */
export function captureEventResolvedAndWatchDeactivated({
	eventId,
	resolutionReason,
	watchId,
}: {
	eventId: string;
	resolutionReason: ResolutionReason;
	watchId: string;
}) {
	posthog.capture("event_resolved_and_watch_deactivated", {
		event_id: eventId,
		resolution_reason: resolutionReason,
		watch_id: watchId,
	});
}

/**
 * Capture when the dashboard receives a websocket payload attributing one or
 * more watches to an event (`addedWatches` from the aggregator).
 *
 * Note: this only fires while a client is connected. For complete match
 * coverage (including offline/background associations), consider also
 * capturing from `packages/functions/src/watch/watch-alert-counter.ts`, which
 * already receives the same `addedWatches` payload server-side.
 */
export function captureEventWatchMatched({
	addedWatchIds,
	eventId,
	updateType,
	watchCount,
	websocketEventType,
}: {
	addedWatchIds: string[];
	eventId: string;
	updateType?: string;
	watchCount?: number;
	websocketEventType: "event.created" | "event.updated";
}) {
	if (addedWatchIds.length === 0) return;

	posthog.capture("event_watch_matched", {
		event_id: eventId,
		watch_count: watchCount,
		watch_id: addedWatchIds[0],
		watch_ids: addedWatchIds,
		websocket_event_type: websocketEventType,
		...(updateType && { update_type: updateType }),
	});
}

/** Capture when the Archive Watch confirmation modal opens. */
export function captureWatchArchiveModalOpened({
	watchId,
}: {
	watchId: string;
}) {
	posthog.capture("watch_archive_modal_opened", { watch_id: watchId });
}

/** Capture when Edit Description (Creates New Watch) is clicked on watch details. */
export function captureWatchCreateFromDescriptionStarted({
	watchId,
}: {
	watchId: string;
}) {
	posthog.capture("watch_create_from_description_started", {
		watch_id: watchId,
	});
}

/** Capture when the Create Watch form mounts. */
export function captureWatchCreateStarted({
	source,
	sourceWatchId,
}: {
	source: WatchCreateSource;
	sourceWatchId?: string;
}) {
	posthog.capture("watch_create_started", {
		source,
		...(sourceWatchId && { source_watch_id: sourceWatchId }),
	});
}

/** Capture when an edit-title or edit-timeframe modal opens. */
export function captureWatchEditModalOpened({
	editType,
	watchId,
}: {
	editType: EditWatchType;
	watchId: string;
}) {
	posthog.capture("watch_edit_modal_opened", {
		edit_type: normalizeWatchEditType(editType),
		watch_id: watchId,
	});
}

/** Capture when the Reactivate Watch modal opens. */
export function captureWatchReactivateModalOpened({
	watchId,
}: {
	watchId: string;
}) {
	posthog.capture("watch_reactivate_modal_opened", { watch_id: watchId });
}

/** Capture when an operator opens a watch from Event Details (Manage Watch link). */
export function captureWatchViewedFromEvent({
	eventId,
	watchId,
}: {
	eventId: string;
	watchId: string;
}) {
	posthog.capture("watch_viewed_from_event", {
		event_id: eventId,
		watch_id: watchId,
	});
}

function normalizeWatchEditType(editType: EditWatchType): string {
	return editType === EditWatchType.TimeframeMinutes
		? "timeframe_minutes"
		: editType;
}
